import {
  AllPointOfInterestTemplateDocument,
  GetVersionsDocument,
  PointOfInterest,
  PointOfInterestTemplateTypeEnum,
  useCreatePointOfInterestTemplateMutation,
  useDeletePointOfInterestTemplateMutation,
  useGetAllPoiIconsForTemplateBuilderQuery,
  usePointOfInterestsCountLazyQuery,
  usePointOfInterestsLazyQuery,
  useUpdatePointOfInterestTemplateMutation,
} from '@/__generated__/graphql';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { abyssColors } from '@/theme/colors';
import { Typography } from '@mui/material';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BasicTabs } from '../../../shared/BasicTabs';
import Button from '../../../shared/Button';
import { PageLoader } from '../../../shared/PageLoader';
import { selectedPOIType } from '../../state';
import { useSnackBarMessage } from '../../utils';
import { PoiFieldsPanel } from './PoiFieldsPanel';
import { FloatingLine, poiTabStyles } from './styles';
import { PoiMarkerLabelPanel } from './PoiMarkerLabelPanel';

export const PoiTemplateForm = ({
  emptyForm,
  handleEmptyForm,
  structureId,
}: {
  emptyForm: boolean;
  handleEmptyForm: (value: boolean) => void;
  structureId: string;
}) => {
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [selectedType, setSelectedType] = useAtom(selectedPOIType);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [selectedPoiCount, setSelectedPoiCount] = useState<number>(0);
  const [examplePoi, setExamplePoi] = useState<PointOfInterest | undefined>(undefined);
  const [allPoiIcons, setAllPoiIcons] = useState<string[]>([]);

  const { data: poiIconsData } = useGetAllPoiIconsForTemplateBuilderQuery({
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    const allIcons = poiIconsData?.getAllPoiIcons;
    if (allIcons && allIcons?.length > 0) {
      const iconNames = allIcons.filter((icon) => icon !== null);
      setAllPoiIcons(iconNames);
    }
  }, [poiIconsData?.getAllPoiIcons]);

  const [fetchPoiCount] = usePointOfInterestsCountLazyQuery();
  const [fetchPois] = usePointOfInterestsLazyQuery();

  useEffect(() => {
    fetchPoiCount({
      variables: {
        input: {
          structureId,
          type: selectedType?.name?.toUpperCase(),
        },
      },
      fetchPolicy: 'cache-first',
    }).then(({ data }) => {
      if (data?.pointOfInterestsCount?.count !== undefined) {
        setSelectedPoiCount(data.pointOfInterestsCount.count);
      }
    });
  }, [fetchPoiCount, selectedType?.name, structureId]);

  useEffect(() => {
    fetchPois({
      variables: {
        input: {
          structureId,
          type: selectedType?.name?.toUpperCase(),
        },
        pageInfo: {
          limit: 1,
        },
      },
      fetchPolicy: 'cache-first',
    }).then(({ data }) => {
      if (data?.pointOfInterests) {
        setExamplePoi(data.pointOfInterests.at(0) as PointOfInterest);
      } else {
        setExamplePoi(undefined);
      }
    });
  }, [fetchPoiCount, fetchPois, selectedType?.name, structureId]);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const [updatePoiTemplate] = useUpdatePointOfInterestTemplateMutation({
    refetchQueries: [AllPointOfInterestTemplateDocument, GetVersionsDocument],
    onCompleted: (response) => {
      if (!response?.updatePointOfInterestTemplate) {
        showSnackBar('An error occurred while updating template', 'error');
        return;
      }
      showSnackBar('Template saved successfully', 'success');
      if (selectedType) {
        setSelectedType({
          ...response?.updatePointOfInterestTemplate,
        });
      }
    },
    onError: (error) => {
      console.error(error);
      showSnackBar(error.message, 'error');
    },
  });

  const [createPointOfInterestTemplate] = useCreatePointOfInterestTemplateMutation({
    refetchQueries: [AllPointOfInterestTemplateDocument, GetVersionsDocument],
    onCompleted: (response) => {
      if (!response?.createPointOfInterestTemplate) {
        showSnackBar('An error occurred while creating template', 'error');
        return;
      }
      showSnackBar('Template created successfully', 'success');
      if (selectedType) {
        setSelectedType({
          ...response?.createPointOfInterestTemplate,
        });
      }
    },
    onError: (error) => {
      console.error(error);
      showSnackBar(error.message, 'error');
    },
  });

  const handleSaveTemplate = useCallback(() => {
    if (selectedType?.id) {
      updatePoiTemplate({
        variables: {
          input: {
            name: selectedType?.name ?? '',
            type: selectedType?.type ?? PointOfInterestTemplateTypeEnum.PointOfInterest,
            pointOfInterestTemplateId: selectedType?.id,
            accentColor: selectedType?.accentColor,
            icon: JSON.parse(JSON.stringify(selectedType?.icon), (key, value) => {
              return key === '__typename' ? undefined : value;
            }),
            fields: selectedType?.fields
              ? selectedType?.fields.map((field) =>
                  JSON.parse(JSON.stringify(field), (key, value) => {
                    if (key === '__typename') return undefined;
                    if (key === 'key' && !value) {
                      return field.name.replace(/\s+/g, '_').toLowerCase();
                    }
                    return value;
                  })
                )
              : [],
            lastModifiedFor: 'Update POI Template',
            ...(selectedType?.markerLabelConfig
              ? {
                  markerLabelConfig: {
                    type: selectedType?.markerLabelConfig?.type,
                    hideName: selectedType?.markerLabelConfig?.hideName ?? false,
                    headerField: selectedType?.markerLabelConfig?.headerField
                      ? {
                          key: selectedType?.markerLabelConfig?.headerField?.key,
                          type: selectedType?.markerLabelConfig?.headerField?.type,
                        }
                      : undefined,
                    fields: selectedType?.markerLabelConfig?.fields.map((field) => ({
                      key: field.key,
                      type: field.type,
                    })),
                  },
                }
              : {}),
          },
        },
      });
    }
  }, [
    selectedType?.id,
    selectedType?.name,
    selectedType?.type,
    selectedType?.accentColor,
    selectedType?.icon,
    selectedType?.fields,
    selectedType?.markerLabelConfig,
    updatePoiTemplate,
  ]);

  const handleCreateTemplate = useCallback(() => {
    if (!selectedType?.name) {
      showSnackBar('Template name is required', 'error');
      return;
    }

    createPointOfInterestTemplate({
      variables: {
        input: {
          accentColor: selectedType?.accentColor,
          icon: JSON.parse(JSON.stringify(selectedType?.icon), (key, value) => {
            return key === '__typename' ? undefined : value;
          }),
          fields: selectedType?.fields
            ? selectedType?.fields.map((field) =>
                JSON.parse(JSON.stringify(field), (key, value) => {
                  return key === '__typename' ? undefined : value;
                })
              )
            : [],
          name: selectedType?.name ?? '',
          type: selectedType?.type ?? PointOfInterestTemplateTypeEnum.PointOfInterest,
          structureId: structureId,
          lastModifiedFor: 'Create POI Template',
        },
      },
    });
  }, [
    createPointOfInterestTemplate,
    selectedType?.accentColor,
    selectedType?.fields,
    selectedType?.icon,
    selectedType?.name,
    selectedType?.type,
    structureId,
  ]);

  const [deletePointOfInterestTemplate, { loading }] = useDeletePointOfInterestTemplateMutation({
    refetchQueries: [AllPointOfInterestTemplateDocument],
    onCompleted: () => {
      setIsDeleteDialogOpen(false);
      showSnackBar('POI template has been deleted successfully', 'success');
    },
    onError: (error) => {
      setIsDeleteDialogOpen(false);
      showSnackBar(`${error.message}`, 'error');
    },
  });

  const handleDeleteTemplate = useCallback(() => {
    if (selectedPoiCount > 0) {
      setIsDeleteDialogOpen(false);
    } else {
      deletePointOfInterestTemplate({
        variables: {
          templateId: selectedType?.id ?? '',
        },
      });
    }
  }, [deletePointOfInterestTemplate, selectedPoiCount, selectedType?.id]);

  const deleteDialogButtonText = useMemo(() => {
    if (selectedPoiCount > 0) {
      return 'Got it';
    }
    return loading ? 'Deleting...' : 'Delete Template';
  }, [loading, selectedPoiCount]);

  const handleTabChange = (selectedTabIndex: number) => {
    setSelectedTab(selectedTabIndex);
  };

  const tabs = useMemo(() => {
    return [
      {
        index: 0,
        label: 'Fields',
        component: (
          <PoiFieldsPanel
            emptyForm={emptyForm}
            handleEmptyForm={handleEmptyForm}
            selectedPoiCount={selectedPoiCount}
            handleCreateTemplate={handleCreateTemplate}
            handleSaveTemplate={handleSaveTemplate}
            setIsDeleteDialogOpen={setIsDeleteDialogOpen}
            allPoiIcons={allPoiIcons}
          />
        ),
      },
      {
        index: 1,
        label: 'Marker Label (Annotation)',
        component: (
          <PoiMarkerLabelPanel handleSaveTemplate={handleSaveTemplate} examplePoi={examplePoi} />
        ),
      },
    ];
  }, [
    allPoiIcons,
    emptyForm,
    handleCreateTemplate,
    handleEmptyForm,
    handleSaveTemplate,
    selectedPoiCount,
    examplePoi,
  ]);

  if (!selectedType) return <PageLoader />;

  return (
    <>
      {isDeleteDialogOpen && (
        <GenericDialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          title="Delete Template"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              {selectedPoiCount > 0
                ? `This template has ${selectedPoiCount} POIs associated with it. Please delete all associated POIs before deleting the template.`
                : 'Are you sure you want to delete this template?'}
            </Typography>
          }
          actions={
            <>
              {selectedPoiCount === 0 && (
                <Button
                  variant="text"
                  onClick={() => setIsDeleteDialogOpen(false)}
                  sx={textButtonStyle}
                >
                  Cancel
                </Button>
              )}
              <Button
                variant="contained"
                onClick={() => handleDeleteTemplate()}
                sx={containedButtonStyle}
                disabled={loading && selectedPoiCount === 0}
              >
                {deleteDialogButtonText}
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}

      <BasicTabs
        tabs={tabs}
        selectedTab={selectedTab}
        changeTab={handleTabChange}
        tabsStyles={poiTabStyles}
        customSeperator={<FloatingLine />}
        renderingStrategy="eager"
      />
    </>
  );
};
